import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Download, AlertTriangle, Package, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const FGBinAudit = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedBin, setExpandedBin] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/wms/fg/bin-audit/multi-sku-report');
      setReport(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleDownloadCSV = async () => {
    try {
      const response = await apiClient.get('/wms/fg/bin-audit/multi-sku-report/csv', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'multi_sku_bin_report.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (error) {
      toast.error('Failed to download CSV');
    }
  };

  const filteredBins = (report?.bins || []).filter(bin => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      bin.bin_code.toLowerCase().includes(term) ||
      bin.primary_sku.item_barcode.toLowerCase().includes(term) ||
      bin.primary_sku.design_name.toLowerCase().includes(term) ||
      bin.minority_skus.some(s =>
        s.item_barcode.toLowerCase().includes(term) ||
        s.design_name.toLowerCase().includes(term)
      )
    );
  });

  const getSeverityColor = (count) => {
    if (count >= 3) return 'bg-red-100 text-red-800 border-red-300';
    return 'bg-amber-100 text-amber-800 border-amber-300';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)} size="sm">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">FG Bin Audit - Multi-SKU Report</h1>
            <p className="text-sm text-gray-500 mt-1">Bins containing items from multiple SKUs that need physical cleanup</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchReport} disabled={loading} data-testid="refresh-report-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" onClick={handleDownloadCSV} disabled={!report?.bins?.length} data-testid="download-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            Download CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {report && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Total FG Bins</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold" data-testid="total-bins-count">{report.total_fg_bins?.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-amber-700">Multi-SKU Bins</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-amber-800" data-testid="multi-sku-count">{report.multi_sku_bins}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Clean Bins</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-green-700">{(report.total_fg_bins - report.multi_sku_bins).toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Health</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-green-700">
                {report.total_fg_bins ? ((1 - report.multi_sku_bins / report.total_fg_bins) * 100).toFixed(1) : 0}%
              </p>
              <p className="text-xs text-gray-500">single-SKU compliance</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by bin code, barcode, or design name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="bin-search-input"
          />
        </div>
      </div>

      {/* Report Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : filteredBins.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Package className="w-12 h-12 mb-3" />
              <p className="text-lg font-medium">
                {report?.multi_sku_bins === 0 ? 'All bins are clean!' : 'No matching bins found'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-100">
                  <TableHead className="font-semibold w-10">#</TableHead>
                  <TableHead className="font-semibold">Bin Code</TableHead>
                  <TableHead className="font-semibold text-center">SKUs</TableHead>
                  <TableHead className="font-semibold text-center">Total Items</TableHead>
                  <TableHead className="font-semibold">Primary SKU (Keep)</TableHead>
                  <TableHead className="font-semibold text-center">Primary Qty</TableHead>
                  <TableHead className="font-semibold">Misplaced SKU(s)</TableHead>
                  <TableHead className="font-semibold text-center">Misplaced Qty</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBins.map((bin, idx) => (
                  <React.Fragment key={bin.bin_code}>
                    <TableRow
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => setExpandedBin(expandedBin === bin.bin_code ? null : bin.bin_code)}
                      data-testid={`bin-row-${bin.bin_code}`}
                    >
                      <TableCell className="text-sm text-gray-500">{idx + 1}</TableCell>
                      <TableCell className="font-mono font-semibold text-sm">{bin.bin_code}</TableCell>
                      <TableCell className="text-center">
                        <Badge className={getSeverityColor(bin.unique_sku_count)}>
                          {bin.unique_sku_count}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center text-sm">{bin.total_items}</TableCell>
                      <TableCell className="text-sm">
                        <span className="font-medium">{bin.primary_sku.design_name}</span>
                        <span className="text-gray-400 text-xs ml-2">{bin.primary_sku.item_barcode}</span>
                      </TableCell>
                      <TableCell className="text-center text-sm font-medium text-green-700">{bin.primary_sku.count}</TableCell>
                      <TableCell className="text-sm">
                        {bin.minority_skus.map((s, i) => (
                          <span key={s.item_barcode}>
                            {i > 0 && ', '}
                            <span className="text-red-700 font-medium">{s.design_name}</span>
                            <span className="text-gray-400 text-xs ml-1">({s.count})</span>
                          </span>
                        ))}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="destructive" className="text-xs">{bin.minority_count}</Badge>
                      </TableCell>
                    </TableRow>
                    {expandedBin === bin.bin_code && (
                      <TableRow className="bg-slate-50">
                        <TableCell colSpan={8} className="p-4">
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 text-sm font-semibold text-gray-600">
                              <AlertTriangle className="w-4 h-4 text-amber-500" />
                              Detailed SKU Breakdown for {bin.bin_code}
                            </div>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="text-xs">Status</TableHead>
                                  <TableHead className="text-xs">SKU Barcode</TableHead>
                                  <TableHead className="text-xs">Design Name</TableHead>
                                  <TableHead className="text-xs">Shade</TableHead>
                                  <TableHead className="text-xs">Size</TableHead>
                                  <TableHead className="text-xs text-center">Qty</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                <TableRow className="bg-green-50">
                                  <TableCell>
                                    <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300 text-xs">Primary</Badge>
                                  </TableCell>
                                  <TableCell className="font-mono text-xs">{bin.primary_sku.item_barcode}</TableCell>
                                  <TableCell className="text-xs font-medium">{bin.primary_sku.design_name}</TableCell>
                                  <TableCell className="text-xs">{bin.primary_sku.shade || '-'}</TableCell>
                                  <TableCell className="text-xs">{bin.primary_sku.size || '-'}</TableCell>
                                  <TableCell className="text-xs text-center font-bold">{bin.primary_sku.count}</TableCell>
                                </TableRow>
                                {bin.minority_skus.map((s) => (
                                  <TableRow key={s.item_barcode} className="bg-red-50">
                                    <TableCell>
                                      <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300 text-xs">Misplaced</Badge>
                                    </TableCell>
                                    <TableCell className="font-mono text-xs">{s.item_barcode}</TableCell>
                                    <TableCell className="text-xs font-medium">{s.design_name}</TableCell>
                                    <TableCell className="text-xs">{s.shade || '-'}</TableCell>
                                    <TableCell className="text-xs">{s.size || '-'}</TableCell>
                                    <TableCell className="text-xs text-center font-bold text-red-700">{s.count}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FGBinAudit;
